(function(window, Image, XMLHttpRequest, XDomainRequest, JSON, console, angular) {
    /**
     * The singleton instance of the WtErrorPigeon that handles both angular and global scopes
     */
    let wtErrorPigeon;

    /**
     * Utility method that validates a value to be a string
     * @param {*} value Value to be validated
     * @returns {boolean}
     */
    const isString = function isString(value) {
        return (typeof value === 'string' || value instanceof String);
    };

    /**
     * Utility method that validates a value to be a plain javascript object (not array, not null, not instance of string, just plain {})
     * @param {*} value Value to be validated
     * @returns {boolean}
     */
    const isPlainObject = function isPlainObject(value) {
        return Object.prototype.toString.call(value) === '[object Object]';
    };

    /**
     * Utility method that validates a value to be an array
     * @param {*} value Value to be validated
     * @returns {boolean}
     */
    const isArray = function isArray(value) {
        return Object.prototype.toString.call(value) === '[object Array]';
    };

    /**
     * Utility method that validates a value to be a valid URL
     * @param {*} value Value to be validated
     * @returns {boolean}
     */
    const isValidUrl = function isValidUrl(value) {
        return /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)(?:\.(?:[a-z\u00a1-\uffff0-9]-*)*[a-z\u00a1-\uffff0-9]+)*(?:\.(?:[a-z\u00a1-\uffff]{2,})))(?::\d{2,5})?(?:[/?#]\S*)?$/i.test(value);
    };

    /**
     * Trims the whitespaces from the beginning and the end of a string
     * @param {*} value The string to be trimmed
     * @returns {string}
     */
    const trim = function trim(value) {
        return value.replace(/^[\s\uFEFF\xA0]+|[\s\uFEFF\xA0]+$/g, '');
    };

    /**
     * Utility method that checks a value to be empty ([], {}, '', null, undefined, 0)
     * @param {*} value Value to be validated
     * @returns {boolean}
     */
    const isEmpty = function isEmpty(value) {
        if (isString()) {
            return !(trim(value));
        }

        if (isArray(value)) {
            return !(value.length);
        }

        if (isPlainObject(value)) {
            return !(Object.getOwnPropertyNames(value).length);
        }

        return !(value);
    };

    /**
     * Utility method that serializes an object into a HTTP GET params string.
     * @param {*} hash The object to be serialised
     * @param {*} prefix Namespace value in case of nested values
     * @returns {string}
     * @example {a: {b: 'c'}, d: 'e'} results in a[b]=c&d=e
     */
    const serialize = function serialize(hash, prefix = null) {
        let str = [];
        let key;

        const getSerializedScalar = function getSerializedScalar(ik, iv) {
            return encodeURIComponent(ik) + '=' + encodeURIComponent(iv);
        };

        const getSerializedObject = function getSerializedObject(ik, iv) {
            return !isEmpty(iv) ? serialize(iv, ik) : getSerializedScalar(ik, '{}');
        };

        for (key in hash) {
            if (hash.hasOwnProperty(key)) {
                let ik = prefix ? prefix + '[' + key + ']' : key;
                let iv = hash[key];

                str.push(
                    isPlainObject(iv) ? getSerializedObject(ik, iv) : getSerializedScalar(ik, iv)
                );
            }
        }

        return trim(str.join('&'));
    };

    /**
     * The polyfill of the ES5 Object.assign
     * @param {Object} target The target object to assign to
     * @returns {Object}
     */
    const assign = function assign(target) {
        const to = Object(target);

        for (let index = 1; index < arguments.length; index++) {
            const nextSource = arguments[index];

            if (nextSource !== null) {
                for (let nextKey in nextSource) {
                    if (Object.prototype.hasOwnProperty.call(nextSource, nextKey)) {
                        to[nextKey] = nextSource[nextKey];
                    }
                }
            }
        }
        return to;
    };

    /**
     * Parses the user agent information and retrieves the browser code name with its version
     * @returns {Object} An object with name and version properties
     */
    const getBrowserInfo = function getBrowserInfo() {
        const ua = navigator.userAgent;
        let match = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
        let tem;

        if (/trident/i.test(match[1])) {
            tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
            return {
                name: 'IE ',
                version: (tem[1] || '')
            };
        }

        if (match[1] === 'Chrome') {
            tem = ua.match(/\bOPR\/(\d+)/);

            if (tem !== null) {
                return {
                    name: 'Opera',
                    version: tem[1]
                };
            }
        }

        match = match[2]
            ? [match[1], match[2]]
            : [navigator.appName, navigator.appVersion, '-?'];

        tem = ua.match(/version\/(\d+)/i);

        if (tem !== null) {
            match.splice(1, 1, tem[1]);
        }

        return {
            name: match[0],
            version: match[1]
        };
    };

    /**
     * Retrieves the stack of an error
     * @param {Error} error The error to retrieve the stack from
     * @returns {string}
     */
    const getErrorStack = function getErrorStack(error) {
        if (!(error instanceof Error)) {
            return '';
        }

        // Rudimentary way of getting the error stack
        return String(
            error.stack || error.stacktrace || error['opera#sourceloc'] || ''
        );
    };

    /**
     * The pigeon configuration.
     * In a normal scenario the pigeon requires minimum configuration, but it is flexible to accept new instructions.
     * @type {Map}
     */
    const wtPigeonConfig = new Map([

        /**
         * The application version
         * @example 0.1.0
         */
        ['version', ''],

        /**
         * The client information
         * @example {}
         */
        ['client', {
            // hostname: window.location.hostname,
            // userAgent: `${getBrowserInfo().name} ${getBrowserInfo().version}`
        }],

        /**
         * The endpoint URL the errors should be sent to
         * @example https://www.example.com/capture
         */
        ['endpointUrl', '//t.mateti.net/t/s'],

        /**
         * The HTTP method to use when sending the pigeon home
         * If logging endpoint does not support CORS, GET method should be used. This method has limits on the amount of info that can be sent,
         * otherwise the POST method can be used
         */
        ['endpointMethod', 'POST'],

        /**
         * Additional tags to be sent along with the error information
         * The pigeon will collect required information from the tracking service, but can be overriden if required
         * @example {locale: 'en-us', timezoneOffset: -60}
         * @note Adding new tags will require changes in the backend to map the new values in ES
         */
        ['tags', {
            product: '',
            account: '',
            user: '',
            customer: '',
            language: ''
        }],

        /**
         * List of hostname regexps, used to identify the sites on which the errors should be captured only.
         * Captures all errors when no whitelist URL is provided
         * @example [/analytics-qa-01\.nbg\.webtrekk\.com/gi, /analytics\.webtrekk\.com/gi] will capture errors on QA and PROD only.
         */
        ['whitelistUrls', []],

        /**
         * List of regexps to be used when identifying which error should be excluded based on error message.
         * Helpful when a specific error should not be sent.
         * @example [/Failed to load resource .* /, /ReferenceError.* /] will not send the errors matching these regexps in the message
         */
        ['ignoreErrors', []]
    ]);

    /**
     * The maximum URL length that is safe to use in the browser
     * @type {number}
     */
    const MAX_URL_LENGTH = 2048;

    /**
     * It limits the angular errors that contain enormous amount. Sending whole error message won't allow to send any additional meta info.
     * It controls the max size of the useful payload (error message and stack)
     * Only applicable when using GET method
     * @type {number}
     */
    const MAX_MESSAGE_LENGTH = 1500;

    /**
     * The quota of how many lines of the error stack should be captured.
     * Note that it is not guaranteed that all lines will be sent as MAX_MESSAGE_LENGTH controls how many chars the message and stack can be
     * Only applicable when using GET method
     * @type {number}
     */
    const MAX_STACK_LINES_COUNT = 5;

    /**
     * The stack of latest sent payloads
     * @type {Set}
     */
    const errorStack = new Set();

    /**
     * @name WtErrorPigeon
     * @description The service that handles errors
     * The service can function both in angular and in global contexts
     * Minimum configuration required for the service to work properly.
     * The logging is performed using a GET request to the configured pigeon home address
     */
    class WtErrorPigeon {
        /**
         * Instructs the pigeon. Convenience method instead of calling each config method separately.
         * @param {Object} config The configuration object
         * @returns {WtErrorPigeon}
         */
        static config(config) {
            for (let property in config) {
                if (!config.hasOwnProperty(property)) {
                    continue;
                }

                switch (property) {
                    case 'version':
                        WtErrorPigeon.setVersion(config[property]);
                        break;

                    case 'endpointUrl':
                        WtErrorPigeon.setEndpointUrl(config[property]);
                        break;

                    case 'endpointMethod':
                        WtErrorPigeon.setEndpointMethod(config[property]);
                        break;

                    case 'tags':
                        WtErrorPigeon.setTags(config[property]);
                        break;

                    case 'whitelistUrls':
                        WtErrorPigeon.setWhitelistUrls(config[property]);
                        break;

                    case 'ignoreErrors':
                        WtErrorPigeon.setIgnoredErrors(config[property]);
                        break;
                }
            }

            return WtErrorPigeon;
        }

        /**
         * Sets the application semver version
         * @param {string} version The semver version of the application to be sent along with the errors
         * @returns {WtErrorPigeon}
         */
        static setVersion(version) {
            if (!version || !isString(version)) {
                return WtErrorPigeon;
            }

            wtPigeonConfig.set('version', version);

            return WtErrorPigeon;
        }

        /**
         * Overrides the pigeon home address
         * @param {string} endpointUrl The endpoint url
         * @returns {WtErrorPigeon}
         */
        static setEndpointUrl(endpointUrl) {
            if (!isValidUrl(endpointUrl)) {
                return WtErrorPigeon;
            }

            wtPigeonConfig.set('endpointUrl', endpointUrl);

            return WtErrorPigeon;
        }

        /**
         * Overrides the HTTP method to be used for logging requests
         * @param {string} method The HTTP method to use
         * @returns {WtErrorPigeon}
         */
        static setEndpointMethod(method) {
            if (method !== 'GET' && method !== 'POST') {
                return WtErrorPigeon;
            }

            wtPigeonConfig.set('endpointMethod', method);

            return WtErrorPigeon;
        }

        /**
         * Sets any additional information to be sent along with the error
         * @param {Object} tags A hash of data. When a non-hash value provided, will reset the tags information.
         * @returns {WtErrorPigeon}
         */
        static setTags(tags) {
            if (!isPlainObject(tags)) {
                wtPigeonConfig.set('tags', {});
                return WtErrorPigeon;
            }

            wtPigeonConfig.set('tags', assign(wtPigeonConfig.get('tags'), tags));

            return WtErrorPigeon;
        }

        /**
         * Whitelisted urls setter. Restricts the errors only from URLs that qualify the regexps from this list.
         * @param {Array} urls A list of regexp to test for whitelisted urls
         * @returns {WtErrorPigeon}
         */
        static setWhitelistUrls(urls) {
            if (!isArray(urls)) {
                return WtErrorPigeon;
            }

            const whitelistUrls = [];

            for (let i = 0; i < urls.length; i++) {
                if (urls[i] && typeof urls[i].test === 'function') {
                    whitelistUrls.push(urls[i]);
                }
            }

            wtPigeonConfig.set('whitelistUrls', whitelistUrls);

            return WtErrorPigeon;
        }

        /**
         * Ignored errors patters setter. Restricts the errors that do not qualify the regexps from this list.
         * @param {Array} errorPatterns A list of regexp to test the blacklisted error messages
         * @returns {WtErrorPigeon}
         */
        static setIgnoredErrors(errorPatterns) {
            if (!isArray(errorPatterns)) {
                return WtErrorPigeon;
            }

            const ignoreErrors = [];

            for (let i = 0; i < errorPatterns.length; i++) {
                if (errorPatterns[i] && typeof errorPatterns[i].test === 'function') {
                    ignoreErrors.push(errorPatterns[i]);
                }
            }

            wtPigeonConfig.set('ignoreErrors', ignoreErrors);

            return WtErrorPigeon;
        }

        /**
         * Angular service initialisation
         * @returns {[string,*]}
         */
        static get $get() {
            return ['$injector', function($injector) {
                wtErrorPigeon.$injector = $injector;

                return wtErrorPigeon;
            }];
        }

        constructor() {
            this.MAX_URL_LENGTH = MAX_URL_LENGTH;
            this.MAX_MESSAGE_LENGTH = MAX_MESSAGE_LENGTH;
            this.MAX_STACK_LINES_COUNT = MAX_STACK_LINES_COUNT;
        }

        /**
         * Captures an exception and sends it to the preconfigured backend url
         * @param {Error} error The error to be sent
         * @param {Object=} extra Additional information to be sent along with the error
         * @returns {WtErrorPigeon}
         */
        sendException(error, extra = {}) {
            if (!(this.isUrlWhitelisted_() && this.isErrorWhitelisted_(error))) {
                return this;
            }

            // Avoids logging same error, i.e. there will be no duplicate error logged during a session
            if (this.isPersistedError_(error)) {
                return this;
            }

            this.onBeforeDispatch_();

            const payload = this.getPayloadParamsHash_(error, extra);

            this.dispatchPigeon_(
                this.buildRequestUrl_(payload), payload
            );

            return this.persistError_(error);
        }

        /**
         * Checks if an error was already logged
         * @param {Error} error The error to be checked
         * @returns {boolean}
         * @private
         */
        isPersistedError_(error) {
            return errorStack.has(String(error));
        }

        /**
         * Stores the error that was dispatched during the current session
         * @param {Error} error The error to be persisted
         * @returns {WtErrorPigeon}
         * @private
         */
        persistError_(error) {
            try {
                errorStack.add(String(error));

                if (errorStack.size > 10) {
                    // Remove the first entry
                    errorStack.delete(
                        errorStack.values().next().value
                    );
                }
            } catch (caughtError) {
                if (console && typeof console.error === 'function') {
                    console.error(caughtError);
                }
            }

            return this;
        }

        /**
         * Hook executed before the pigeon is dispatched. Allows the pigeon to enrich the payload with information from the external sources
         * @returns {WtErrorPigeon}
         * @private
         */
        onBeforeDispatch_() {
            const tags = wtPigeonConfig.get('tags') || {};

            if (this.$injector) {
                const trackingService = this.$injector.get('trackingService');

                const product = trackingService.getProduct();
                const account = trackingService.getAccount();
                const user = trackingService.getUser();
                const customer = trackingService.getCustomer();
                const language = trackingService.getLanguage();

                // Information set in the angular world will be accessible by the pigeon instances outside angular.
                // Defaults to existing or empty value if trackingService does not have the info
                WtErrorPigeon.setTags({
                    product: product || tags.product || '',
                    account: account || tags.account || '',
                    user: user || tags.user || '',
                    customer: customer || tags.customer || '',
                    language: language || tags.language || ''
                });
            }

            if (!wtPigeonConfig.get('version') && window.wtAppVersion) {
                WtErrorPigeon.setVersion(window.wtAppVersion);
            }

            return this;
        }

        /**
         * Log the error using the preconfigured transport
         * @param {string} url The final URL to log the error to
         * @param {Object=} payload The payload to be sent in case of non-GET requests
         * @returns {WtErrorPigeon}
         * @private
         */
        dispatchPigeon_(url, payload = {}) {
            if (!isValidUrl(url) || !Image || !isPlainObject(payload)) {
                return this;
            }

            if (this.isGetRequest_()) {
                (new Image()).src = url;
            } else {
                try {
                    const isAncient = this.isAncientBrowser_();
                    const request = isAncient ? new XDomainRequest() : new XMLHttpRequest();
                    const sPayload = JSON.stringify(payload);

                    request.open(wtPigeonConfig.get('endpointMethod'), wtPigeonConfig.get('endpointUrl'));

                    if (!isAncient) {
                        request.setRequestHeader('Content-Type', 'application/json');
                    }

                    request.send(sPayload);
                } catch (error) {
                    if (console && typeof console.error === 'function') {
                        console.error(error);
                    }
                }
            }

            return this;
        }

        /**
         * Returns the current URL the browser is pointing to
         * @returns {string}
         * @private
         */
        getCurrentLocation_() {
            return window.location.href;
        }

        /**
         * Validate if the currently visited URL is whitelisted and allows to log the errors occurred on this page
         * @returns {boolean}
         * @private
         */
        isUrlWhitelisted_() {
            const qualifiedUrls = wtPigeonConfig.get('whitelistUrls');
            let qualifies = (qualifiedUrls.length === 0); // When there was no whitelisted url, by default all urls are whitelisted

            for (let i = 0; i < qualifiedUrls.length; i++) {
                if (qualifiedUrls[i].test(this.getCurrentLocation_())) {
                    qualifies = true;
                }
            }

            return qualifies;
        }

        /**
         * Validates if the error content is not blacklisted. The pigeon can be configured to ignore certain error patterns.
         * @param {Error} error The error instance
         * @returns {boolean}
         * @private
         */
        isErrorWhitelisted_(error) {
            const ignoreErrors = wtPigeonConfig.get('ignoreErrors');
            const message = error instanceof Error ? error.message : String(error);
            let qualifies = true;

            for (let i = 0; i < ignoreErrors.length; i++) {
                qualifies = qualifies && !ignoreErrors[i].test(message);
            }

            return qualifies;
        }

        /**
         * Identifies what method to use when sending the error to the backend
         * Defaults to GET requests when the browser does not support CORS POST AJAX
         * @returns {boolean}
         * @private
         */
        isGetRequest_() {
            return (wtPigeonConfig.get('endpointMethod') === 'GET') || !(XMLHttpRequest || XDomainRequest);
        }

        /**
         * Identifies browsers that do not support XMLHttpRequest or the XMLHttpRequest implementation does not support CORS POST requests
         * @returns {boolean}
         * @private
         */
        isAncientBrowser_() {
            return (typeof XMLHttpRequest !== 'undefined' && typeof (new XMLHttpRequest().withCredentials) === 'undefined')
                || (typeof XDomainRequest !== 'undefined');
        }

        /**
         * Constructs the payload with the collected information to be sent to the logging endpoint
         * @param {Error} error The error instance
         * @param {Object=} extra An object with additional information specific to this error
         * @returns {*}
         * @private
         */
        getPayloadParamsHash_(error, extra = {}) {
            const params = {};

            params.type = error instanceof Error ? error.name : '';

            params.msg = (error instanceof Error ? error.message : String(error));

            if (this.isGetRequest_()) {
                params.msg = params.msg.slice(0, MAX_MESSAGE_LENGTH);
            }

            params.stack = getErrorStack(error);

            if (this.isGetRequest_()) {
                params.stack = params.stack.split('\n')

                    // Grab the preconfigured lines from the stack
                    .slice(0, MAX_STACK_LINES_COUNT).join('\n')

                    // The error message and the stack share the same quota
                    .slice(0, (MAX_MESSAGE_LENGTH - params.msg.length));
            }

            params.version = wtPigeonConfig.get('version');

            params.tags = wtPigeonConfig.get('tags');

            params.client = assign(
                this.isGetRequest_()
                    ? {}
                    : {referrer: this.getCurrentLocation_()},
                wtPigeonConfig.get('client')
            );

            // Any info sent via extra information will stringified to allow dynamic addition of properties
            params.context = JSON.stringify(
                isPlainObject(extra) ? extra : {}
            );

            return this.flattenedParamsHash_(params);
        }

        /* eslint no-unused-vars: 0 */
        /**
         * Flattens the nested payload
         * @param {Object} params The nested payload to be flattened
         * @param {string} prefix The prefix to be used when building the keys {a: {b: 'c'}} => {a: {}, b: 'c'} vs {a: {}, a_b: 'c'}
         * @returns {{}}
         * @private
         */
        flattenedParamsHash_(params, prefix = '') {
            const flattenedParams = {};
            let circularReference = false;

            // This method is recursive. Detect any circular reference at an early stage.
            try {
                JSON.stringify(params);
            } catch (err) {
                circularReference = true;
            }

            if (!isPlainObject(params) || isEmpty(params) || circularReference) {
                return flattenedParams;
            }

            for (let key in params) {
                if (!params.hasOwnProperty(key)) {
                    continue;
                }

                let value = params[key];

                // Will generate 'tags_user' key instead of 'user'
                // let newKey = prefix ? prefix + '_' + key : key;
                let newKey = key;

                if (isPlainObject(value)) {
                    assign(flattenedParams, this.flattenedParamsHash_(value, newKey));
                } else if (isArray(value)) {
                    flattenedParams[newKey] = value.join(', ');
                } else {
                    flattenedParams[newKey] = value;
                }
            }

            return flattenedParams;
        }

        /**
         * Constructs the home address url by serializing the payload
         * For a POST request the payload will be constructed in the dispatch method
         * @param {Object=} payload The payload to be sent
         * @returns {string}
         * @private
         */
        buildRequestUrl_(payload = {}) {
            const homeUrl = trim(wtPigeonConfig.get('endpointUrl'));

            if (!this.isGetRequest_()) {
                return homeUrl;
            }

            const serializedPayload = trim(serialize(payload));

            return `${homeUrl}?${serializedPayload}`.slice(0, MAX_URL_LENGTH);
        }
    }

    /**
     * Creates the instance that handles global contexts exceptions (uncaught by angular)
     */
    wtErrorPigeon = new WtErrorPigeon();

    /**
     * Registers the error handler outside of angular scope
     * @param {string} message The message associated with the error
     * @param {string} url The URL of the script or document associated with the error
     * @param {number|null=} line The line number
     * @param {number|null=} column The column number
     * @param {Error|null=} iError The original error object
     */
    window.onerror = function onerror(message, url, line = null, column = null, iError = null) {
        // The error object is not returned by all clients (Older IE/Webkit browsers)
        const error = iError instanceof Error ? iError : new Error(message);

        wtErrorPigeon.sendException(error);
    };

    /**
     * Exposes the service in the global scope for testing purposes
     * @type {WtErrorPigeon}
     */
    if (window.__karma__) {
        window.wtErrorPigeon = wtErrorPigeon;
    }

    /**
     * Registers the angular error handler provider
     */
    angular.module('wtApp.wtTracking.errorHandler').provider('wtErrorPigeon', function WtErrorPigeonProvider() {
        return WtErrorPigeon;
    });
})(window, window.Image, window.XMLHttpRequest, window.XDomainRequest, window.JSON, window.console, window.angular);
